import { ArrowDownIcon, ArrowTurnDownLeftIcon, ArrowUpIcon } from "@heroicons/react/20/solid";
import { type FC, type ReactNode } from "react";

import { FAILURE_TAGS, REVIEW_KEYS, SCORE_VALUES } from "@components/EvalReview/constants";

/** One key, drawn as a key. */
const Key: FC<{ children: ReactNode; label?: string }> = ({ children, label }) => (
  <kbd
    aria-label={label}
    className="inline-flex min-w-5 items-center justify-center rounded border border-border-medium bg-bg-surface px-1 py-0.5 font-sans text-xs leading-4 text-text-secondary"
  >
    {children}
  </kbd>
);

const Entry: FC<{ keys: ReactNode; children: ReactNode }> = ({ keys, children }) => (
  <span className="flex items-center gap-x-1.5">
    <span className="flex items-center gap-x-0.5">{keys}</span>
    <span className="text-text-tertiary">{children}</span>
  </span>
);

/**
 * What the keys do, shown as keys.
 *
 * It replaces a run-on line — "Keys: 1–5 score · j/k move · q w e r t tags ·
 * n notes · Enter save" — where the reader had to work out that "j/k" meant
 * up and down and that the bare letters were the tag shortcuts. Each binding
 * is now drawn as the key itself, with the arrow and return glyphs carrying
 * the meanings that have a symbol.
 *
 * Bindings come from REVIEW_KEYS and FAILURE_TAGS, the same source the
 * handler reads, so the legend cannot describe a key that does nothing.
 */
export const KeyLegend: FC = () => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
    <Entry
      keys={
        <>
          <Key>{SCORE_VALUES[0]}</Key>
          <span className="text-text-tertiary">–</span>
          <Key>{SCORE_VALUES[SCORE_VALUES.length - 1]}</Key>
        </>
      }
    >
      score
    </Entry>

    <Entry
      keys={
        <>
          <Key label="Up arrow">
            <ArrowUpIcon className="size-3" aria-hidden="true" />
          </Key>
          <Key label="Down arrow">
            <ArrowDownIcon className="size-3" aria-hidden="true" />
          </Key>
        </>
      }
    >
      next criterion
    </Entry>

    <Entry
      keys={
        <>
          <Key>{FAILURE_TAGS[0].key}</Key>
          <span className="text-text-tertiary">–</span>
          <Key>{FAILURE_TAGS[FAILURE_TAGS.length - 1].key}</Key>
        </>
      }
    >
      flag what went wrong
    </Entry>

    <Entry keys={<Key>{REVIEW_KEYS.NOTES}</Key>}>jump to Why</Entry>

    <Entry
      keys={
        <Key label="Enter">
          <ArrowTurnDownLeftIcon className="size-3" aria-hidden="true" />
        </Key>
      }
    >
      save
    </Entry>
  </div>
);
